import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV, LOGO_URL } from "@/data";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSubmenuOpen(false);
    if (!location.hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);

  const isActive = (to) => location.pathname === to;

  const handleHashNav = (e, to) => {
    e.preventDefault();
    const [path, hash] = to.split("#");
    if (location.pathname !== path && path) {
      navigate(to);
    } else {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 bg-black ${
        scrolled
          ? "border-b border-[#D90000]/30 py-3 shadow-2xl shadow-black/50"
          : "border-b border-white/5 py-6"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
        {/* LOGO LEFT · 3D effect */}
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 group" style={{ perspective: "1000px" }}>
          <img
            src={LOGO_URL}
            alt="VistaWash Detailing Automobile"
            className="w-auto object-contain transition-transform duration-500 group-hover:scale-110"
            style={{
              height: "72px",
              filter:
                "drop-shadow(2px 2px 0 #ff2424) drop-shadow(3px 3px 0 #c80000) drop-shadow(4px 5px 0 #8a0000) drop-shadow(6px 10px 18px rgba(217,0,0,0.7)) brightness(1.05) contrast(1.05) saturate(1.15)",
              transform: "perspective(1000px) rotateX(12deg) rotateY(-10deg)",
              transformStyle: "preserve-3d",
            }}
          />
        </Link>

        {/* NAV CENTERED */}
        <nav className="hidden lg:flex items-center gap-10 flex-1 justify-center">
          {NAV.map((n) =>
            n.children ? (
              <div
                key={n.label}
                className="relative"
                onMouseEnter={() => setSubmenuOpen(true)}
                onMouseLeave={() => setSubmenuOpen(false)}
              >
                <button
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className="flex items-center gap-1.5 text-[13px] uppercase tracking-[0.18em] font-semibold text-white hover:text-[#D90000] transition-colors"
                >
                  {n.label} <ChevronDown size={12} />
                </button>
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all ${
                    submenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
                  }`}
                >
                  <div className="bg-black border border-[#D90000]/30 min-w-[280px] py-2">
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`subnav-${c.to.replace(/\//g, "")}`}
                        className={`block px-5 py-3 text-sm uppercase tracking-[0.12em] transition-colors hover:bg-[#D90000] hover:text-white ${
                          isActive(c.to) ? "text-[#D90000]" : "text-white/85"
                        }`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : n.hash ? (
              <a
                key={n.label}
                href={n.to}
                onClick={(e) => handleHashNav(e, n.to)}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-[13px] uppercase tracking-[0.18em] font-semibold text-white hover:text-[#D90000] transition-colors"
              >
                {n.label}
              </a>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-[13px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                  isActive(n.to) ? "text-[#D90000]" : "text-white hover:text-[#D90000]"
                }`}
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        {/* CTA RIGHT */}
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-all border border-[#D90000] hover:border-white"
          >
            Demander un devis <ArrowUpRight size={14} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-black border-t border-[#D90000]/20 max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.label} className="py-2 border-b border-white/5">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-3 pt-2 font-bold">
                    {n.label}
                  </div>
                  {n.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-subnav-${c.to.replace(/\//g, "")}`}
                      className="block py-3 text-base uppercase tracking-[0.1em]"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : n.hash ? (
                <a
                  key={n.label}
                  href={n.to}
                  onClick={(e) => handleHashNav(e, n.to)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="py-4 text-lg uppercase tracking-[0.15em] font-bold border-b border-white/5"
                >
                  {n.label}
                </a>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="py-4 text-lg uppercase tracking-[0.15em] font-bold border-b border-white/5"
                >
                  {n.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              data-testid="mobile-cta"
              className="mt-6 bg-[#D90000] text-white text-[12px] uppercase tracking-[0.2em] font-bold px-6 py-4 inline-block text-center"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
